import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { ToastBody } from 'react-bootstrap'
import Toast from 'react-bootstrap/Toast'
import ToastContainer from 'react-bootstrap/ToastContainer'
import { BootstrapVariantType, ChildrenType } from '../types/component-props'

type ShowNotificationType = {
  title?: string
  message: string
  variant?: BootstrapVariantType
  delay?: number
  autohide?: boolean
}

type ToastrProps = {
  show: boolean
  onClose?: () => void
} & ShowNotificationType

type NotificationContextType = {
  showNotification: (config: ShowNotificationType) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

function Toastr({ show, message, onClose, variant = 'light', delay, autohide = true }: Readonly<ToastrProps>) {
  return (
    <ToastContainer className="p-3 position-fixed" position="top-end" style={{ zIndex: 9999 }}>
      <Toast bg={variant} delay={delay} show={show} onClose={onClose} autohide={autohide}>
        <ToastBody className={['dark', 'danger', 'success', 'primary'].includes(variant) ? 'text-white' : ''}>
          {message}
        </ToastBody>
      </Toast>
    </ToastContainer>
  )
}

export function useNotificationContext() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotificationContext must be used within an NotificationProvider')
  }
  return context
}

export function NotificationProvider({ children }: ChildrenType) {
  const defaultConfig = {
    show: false,
    message: '',
    title: '',
    delay: 2000,
  }

  const [config, setConfig] = useState<ToastrProps>(defaultConfig)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const hideNotification = useCallback(() => {
    setConfig((prev) => ({ ...prev, show: false }))
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const showNotification = useCallback(({ title, message, variant, delay, autohide }: ShowNotificationType) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    const isDanger = variant === 'danger'
    const finalAutoHide = autohide ?? !isDanger
    const finalDelay = delay ?? (isDanger ? 10000 : 3000)

    setConfig({
      show: true,
      title,
      message,
      variant: variant ?? 'light',
      onClose: hideNotification,
      delay: finalDelay,
      autohide: finalAutoHide,
    })

    if (finalAutoHide) {
      timeoutRef.current = setTimeout(() => {
        setConfig((prev) => ({ ...prev, show: false }))
        timeoutRef.current = null
      }, finalDelay)
    }
  }, [hideNotification])

  const contextValue = useMemo(() => ({ showNotification }), [showNotification])

  return (
    <NotificationContext.Provider value={contextValue}>
      <Toastr {...config} />
      {children}
    </NotificationContext.Provider>
  )
}
